import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

let appInsights: ApplicationInsights | null = null;
let reactPlugin: ReactPlugin | null = null;
let isInitialized = false;

export function initializeAppInsights(): {
  appInsights: ApplicationInsights | null;
  reactPlugin: ReactPlugin | null;
} {
  const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING;

  if (!connectionString) {
    console.warn('Application Insights connection string not configured. Telemetry will be disabled.');
    return { appInsights: null, reactPlugin: null };
  }

  if (isInitialized && appInsights && reactPlugin) {
    return { appInsights, reactPlugin };
  }

  reactPlugin = new ReactPlugin();

  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      extensions: [reactPlugin],
      enableAutoRouteTracking: true,
      enableCorsCorrelation: true,
      enableAjaxPerfTracking: false,
      enableUnhandledPromiseRejectionTracking: true,
      disableFetchTracking: false,
      disableAjaxTracking: false,
      autoTrackPageVisitTime: false,
      enableSessionStorageBuffer: true,
      samplingPercentage: 50,
    }
  });

  appInsights.loadAppInsights();

  appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
    if (envelope.tags) {
      envelope.tags['ai.cloud.role'] = 'Entire.WebClient';
      envelope.tags['ai.cloud.roleInstance'] = window.location.hostname;
    }
  });

  isInitialized = true;

  return { appInsights, reactPlugin };
}

export function getAppInsights(): ApplicationInsights | null {
  return appInsights;
}

export function getReactPlugin(): ReactPlugin | null {
  return reactPlugin;
}

export function setAuthenticatedUser(userId: string, accountId?: string): void {
  if (appInsights) {
    appInsights.setAuthenticatedUserContext(userId, accountId, true);
  }
}

export function clearAuthenticatedUser(): void {
  if (appInsights) {
    appInsights.clearAuthenticatedUserContext();
  }
}

export function trackEvent(
  name: string,
  properties?: Record<string, string>,
  measurements?: Record<string, number>
): void {
  if (appInsights) {
    appInsights.trackEvent({ name }, { ...properties, ...measurements });
  }
}

export function trackException(error: Error, properties?: Record<string, string>): void {
  if (appInsights) {
    appInsights.trackException({ exception: error }, properties);
  }
}

export function trackPageView(
  name?: string,
  uri?: string,
  properties?: Record<string, string>
): void {
  if (appInsights) {
    appInsights.trackPageView({ name, uri, properties });
  }
}

export function trackMetric(
  name: string,
  average: number,
  properties?: Record<string, string>
): void {
  if (appInsights) {
    appInsights.trackMetric({ name, average }, properties);
  }
}

export function trackDependency(
  id: string,
  method: string,
  absoluteUrl: string,
  duration: number,
  success: boolean,
  responseCode: number,
  properties?: Record<string, string>
): void {
  if (appInsights) {
    appInsights.trackDependencyData({
      id,
      name: `${method} ${absoluteUrl}`,
      duration,
      success,
      responseCode,
      type: 'Ajax',
      target: new URL(absoluteUrl).hostname,
      properties
    });
  }
}

export function startTrackEvent(name: string): void {
  if (appInsights) {
    appInsights.startTrackEvent(name);
  }
}

export function stopTrackEvent(
  name: string,
  properties?: Record<string, string>,
  measurements?: Record<string, number>
): void {
  if (appInsights) {
    appInsights.stopTrackEvent(name, properties, measurements);
  }
}

export function flush(): void {
  if (appInsights) {
    appInsights.flush();
  }
}
